import { useState, useEffect, useMemo } from 'react';
import { Alert, Box, MenuItem, useMediaQuery, useTheme } from '@mui/material';

import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomAutoComplete,
  HorizontalForm,
  VerticalForm,
  CustomTextArea,
} from '../../../common/components';
import BeneficiarySelectWithCreate from '../../common/components/BeneficiarySelectWithCreate';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
  CURRENCY_OPTIONS,
} from '../../constants/financeConstants';

import { useCreateBankMovement, useUpdateBankMovement } from '../../../hooks/useBankMovements';
import { useMovementTypes } from '../../../hooks/useMovementTypes';
import { useMovementConcepts } from '../../../hooks/useMovementConcepts';
import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { useAuth } from '../../../hooks/useAuth';
import { formatAccountType, formatBankName, formatFundAccountLabel } from '../utils/formatters';
import { toArgentinaISODate } from '../../utils/financeFormatters';

const VALIDATION_START_DATE = '2025-11-08';

const MovementDialog = ({ open, onClose, movement = null, account, editMode = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  // Mismo criterio que el backend (auth_utils.is_current_user_admin): flag o rol "Administrador".
  const isAdmin = Boolean(user?.is_administrator) || user?.role?.name === 'Administrador';
  // Hora Argentina: el backend valida "hoy" con hora Argentina, no UTC.
  const today = toArgentinaISODate();
  const isCashAccount = account?.fund_type === 'cash' || Boolean(movement?.uuid_cash_account);
  const isPastDate = (value) => Boolean(value) && value < today;

  const [formData, setFormData] = useState({
    uuid_movement_type: '',
    uuid_movement_concept: '',
    emission_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    amount: '',
    concept: '',
    voucher_number: '',
    uuid_legal_entity: '',
    cost_behavior: '',
    cost_nature: '',
    cost_traceability: '',
    cost_area: '',
    payment_method: '',
    uuid_cash_account: '',
    currency: 'ARS',
    exchange_rate_to_ars: '',
    amount_ars: '',
    is_reconciled: false,
    observations: '',
  });

  const [errors, setErrors] = useState({});

  const { data: legalEntities = [] } = useLegalEntities();
  const { data: movementTypes = [] } = useMovementTypes();
  const { data: movementConcepts = [] } = useMovementConcepts();
  const availableMovementTypes = useMemo(
    () =>
      movementTypes.filter((type) => {
        const isSelectedType = type.uuid === formData.uuid_movement_type;
        if (type.is_active === false && !isSelectedType) {
          return false;
        }

        if (account) {
          return type.is_bank_movement_type !== false || isSelectedType;
        }

        return type.is_bank_movement_type === false || isSelectedType;
      }),
    [account, formData.uuid_movement_type, movementTypes]
  );

  const createMovementMutation = useCreateBankMovement();
  const updateMovementMutation = useUpdateBankMovement();

  useEffect(() => {
    if (open) {
      if (editMode && movement) {
        setFormData({
          uuid_movement_type: movement.uuid_movement_type || '',
          uuid_movement_concept: movement.uuid_movement_concept || '',
          emission_date: movement.emission_date
            ? new Date(movement.emission_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          due_date: movement.due_date
            ? new Date(movement.due_date).toISOString().split('T')[0]
            : movement.emission_date
            ? new Date(movement.emission_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          amount: movement.amount?.toString() || '',
          concept: movement.concept || '',
          voucher_number: movement.voucher_number || '',
          uuid_legal_entity: movement.uuid_legal_entity || '',
          cost_behavior: movement.cost_behavior || '',
          cost_nature: movement.cost_nature || movement.cost_traceability || '',
          cost_traceability: movement.cost_nature || movement.cost_traceability || '',
          cost_area: movement.cost_area || '',
          payment_method: movement.payment_method || '',
          uuid_cash_account: movement.uuid_cash_account || '',
          currency: movement.currency || 'ARS',
          exchange_rate_to_ars: movement.exchange_rate_to_ars?.toString() || '',
          amount_ars: movement.amount_ars?.toString() || '',
          is_reconciled: movement.is_reconciled || false,
          observations: movement.observations || '',
        });
      } else {
        setFormData({
          uuid_movement_type: '',
          uuid_movement_concept: '',
          emission_date: new Date().toISOString().split('T')[0],
          due_date: new Date().toISOString().split('T')[0],
          amount: '',
          concept: '',
          voucher_number: '',
          uuid_legal_entity: '',
          cost_behavior: '',
          cost_nature: '',
          cost_traceability: '',
          cost_area: '',
          payment_method: '',
          uuid_cash_account: '',
          currency: 'ARS',
          exchange_rate_to_ars: '',
          amount_ars: '',
          is_reconciled: false,
          observations: '',
        });
      }
      setErrors({});
    }
  }, [open, editMode, movement]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Si se está cambiando el concepto de movimiento, actualizar también el campo concept
    if (name === 'uuid_movement_concept') {
      const selectedConcept = movementConcepts.find((c) => c.uuid === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        concept: selectedConcept ? selectedConcept.name : prev.concept,
      }));
    } else if (name === 'cost_nature' || name === 'cost_traceability') {
      setFormData((prev) => ({
        ...prev,
        cost_nature: value,
        cost_traceability: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const enforceDueDateOrder =
      !editMode ||
      (formData.emission_date &&
        new Date(formData.emission_date) >= new Date(VALIDATION_START_DATE));

    if (!formData.emission_date) {
      newErrors.emission_date = true;
    }
    if (!formData.uuid_movement_type) {
      newErrors.uuid_movement_type = true;
    }
    if (!formData.amount || parseFloat(formData.amount) === 0) {
      newErrors.amount = true;
    }
    if (!formData.due_date) {
      newErrors.due_date = 'required';
    } else if (
      enforceDueDateOrder &&
      formData.emission_date &&
      new Date(formData.due_date) < new Date(formData.emission_date)
    ) {
      newErrors.due_date = 'before';
    }
    if (!formData.cost_behavior) {
      newErrors.cost_behavior = true;
    }
    if (!(formData.cost_nature || formData.cost_traceability)) {
      newErrors.cost_nature = true;
    }
    if (!formData.cost_area) {
      newErrors.cost_area = true;
    }

    if (isCashAccount && !isAdmin && (isPastDate(formData.emission_date) || isPastDate(formData.due_date))) {
      newErrors.emission_date = 'past_cash';
      newErrors.due_date = 'past_cash';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const bankAccountId = isCashAccount ? null : account?.uuid ?? movement?.uuid_bank_account ?? null;
      const cashAccountId = isCashAccount ? account?.uuid : movement?.uuid_cash_account ?? null;
      const movementData = {
        uuid_bank_account: bankAccountId,
        uuid_cash_account: cashAccountId,
        uuid_movement_type: formData.uuid_movement_type || null,
        uuid_movement_concept: formData.uuid_movement_concept || null,
        emission_date: new Date(formData.emission_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        amount: parseFloat(formData.amount),
        concept: formData.concept.trim() || null,
        voucher_number: formData.voucher_number.trim() || null,
        uuid_legal_entity: formData.uuid_legal_entity || null,
        cost_behavior: formData.cost_behavior || null,
        cost_nature: formData.cost_nature || formData.cost_traceability || null,
        cost_traceability: formData.cost_nature || formData.cost_traceability || null,
        cost_area: formData.cost_area || null,
        payment_method: formData.payment_method || null,
        currency: formData.currency || 'ARS',
        exchange_rate_to_ars: formData.exchange_rate_to_ars ? parseFloat(formData.exchange_rate_to_ars) : null,
        amount_ars: formData.amount_ars ? parseFloat(formData.amount_ars) : null,
        is_reconciled: formData.is_reconciled,
        observations: formData.observations.trim() || null,
      };

      if (editMode) {
        await updateMovementMutation.mutateAsync({
          id: movement.uuid,
          data: movementData,
        });
      } else {
        await createMovementMutation.mutateAsync(movementData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving movement:', error);
    }
  };

  const isLoading = createMovementMutation.isPending || updateMovementMutation.isPending;
  
  const dueDateHelperText =
    errors.due_date === 'past_cash'
      ? 'Solo un Administrador puede cargar o modificar movimientos de caja con fechas anteriores a hoy'
      : errors.due_date === 'before'
      ? 'La fecha de vencimiento no puede ser anterior a la fecha de emisión'
      : errors.due_date
      ? 'La fecha de vencimiento es obligatoria'
      : undefined;
  const emissionDateHelperText =
    errors.emission_date === 'past_cash'
      ? 'Solo un Administrador puede cargar o modificar movimientos de caja con fechas anteriores a hoy'
      : undefined;
  const classificationHelperText = 'La clasificación es obligatoria';

  return (
    <DialogContainer open={open} onClose={onClose} size={isMobile ? 'fullScreen' : 'medium'}>
      <DialogTitle>{editMode ? 'Editar Movimiento' : 'Ingresar un movimiento'}</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {account && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <strong>{account.fund_type === 'cash' ? 'Caja:' : 'Banco:'}</strong>{' '}
                {account.fund_type === 'cash' ? account.name : formatBankName(account.bank_name)}
              </Box>
              <Box>
                <strong>{account.fund_type === 'cash' ? 'Cuenta:' : 'Cuenta:'}</strong>{' '}
                {account.fund_type === 'cash' ? formatFundAccountLabel(account) : account.account_number}
              </Box>
              <Box>
                <strong>Tipo:</strong> {formatAccountType(account.account_type)}
              </Box>
            </Box>
          </Box>
        )}

        {isCashAccount && !isAdmin && (isPastDate(formData.emission_date) || isPastDate(formData.due_date)) && (
          <Alert severity="warning">
            Solo un Administrador puede guardar movimientos de caja con fechas anteriores a hoy.
          </Alert>
        )}

        {isMobile ? (
          <VerticalForm>
            <CustomAutoComplete
              label="Tipo de movimiento"
              name="uuid_movement_type"
              placeholder="Seleccionar tipo"
              value={formData.uuid_movement_type}
              onChange={handleInputChange}
              error={errors.uuid_movement_type}
              required
            >
              <MenuItem value="">Seleccionar tipo</MenuItem>
              {availableMovementTypes
                .map((type) => (
                  <MenuItem key={type.uuid} value={type.uuid}>
                    {type.description}
                  </MenuItem>
                ))}
            </CustomAutoComplete>

            <CustomAutoComplete
              label="Concepto de movimiento"
              name="uuid_movement_concept"
              placeholder="Seleccionar concepto"
              value={formData.uuid_movement_concept}
              onChange={handleInputChange}
            >
              <MenuItem value="">Sin concepto</MenuItem>
              {movementConcepts
                .filter((concept) => concept.is_active !== false)
                .map((concept) => (
                  <MenuItem key={concept.uuid} value={concept.uuid}>
                    {concept.name}
                  </MenuItem>
                ))}
            </CustomAutoComplete>

            <CustomTextField
              label="Fecha de emisión"
              name="emission_date"
              type="date"
              value={formData.emission_date}
              onChange={handleInputChange}
              error={Boolean(errors.emission_date)}
              helperText={emissionDateHelperText}
              required
              shrinkLabel
            />

            <CustomTextField
              label="Fecha de vencimiento"
              name="due_date"
              type="date"
              value={formData.due_date}
              onChange={handleInputChange}
              error={Boolean(errors.due_date)}
              helperText={dueDateHelperText}
              required
              shrinkLabel
            />

            <CustomTextField
              label="Importe"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              error={errors.amount}
              required
              inputProps={{ step: 0.01 }}
            />

            <CustomTextField
              label="Concepto"
              name="concept"
              value={formData.concept}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
            <CustomAutoComplete
              label="Fijo/Variable"
              name="cost_behavior"
              value={formData.cost_behavior}
              onChange={handleInputChange}
              error={Boolean(errors.cost_behavior)}
              helperText={errors.cost_behavior ? classificationHelperText : ''}
              required
            >
              {COST_BEHAVIOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </CustomAutoComplete>
            <CustomAutoComplete
              label="Ordinario/Extraordinario"
              name="cost_nature"
              value={formData.cost_nature || formData.cost_traceability}
              onChange={handleInputChange}
              error={Boolean(errors.cost_nature)}
              helperText={errors.cost_nature ? classificationHelperText : ''}
              required
            >
              {COST_TRACEABILITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </CustomAutoComplete>
            <CustomAutoComplete
              label="Área"
              name="cost_area"
              value={formData.cost_area}
              onChange={handleInputChange}
              error={Boolean(errors.cost_area)}
              helperText={errors.cost_area ? classificationHelperText : ''}
              required
            >
              {COST_AREA_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </CustomAutoComplete>
          </VerticalForm>
        ) : (
          <HorizontalForm sx={{ pt: 0 }} variant="twoRows">
            <Box>
              <CustomAutoComplete
                label="Tipo de movimiento"
                name="uuid_movement_type"
                placeholder="Seleccionar tipo"
                value={formData.uuid_movement_type}
                onChange={handleInputChange}
                error={errors.uuid_movement_type}
                required
                sx={{ maxWidth: '350px', mr: 1.5 }}
              >
                <MenuItem value="">Seleccionar tipo</MenuItem>
                {availableMovementTypes
                  .map((type) => (
                    <MenuItem key={type.uuid} value={type.uuid}>
                      {type.description}
                    </MenuItem>
                  ))}
              </CustomAutoComplete>

              <CustomTextField
                label="Num. comprob."
                name="voucher_number"
                value={formData.voucher_number}
                onChange={handleInputChange}
                sx={{ maxWidth: '350px', mr: 1.5  }}
              />

              <CustomAutoComplete
                label="Concepto de movimiento"
                name="uuid_movement_concept"
                placeholder="Seleccionar concepto"
                value={formData.uuid_movement_concept}
                onChange={handleInputChange}
                sx={{ maxWidth: '350px' }}
              >
                <MenuItem value="">Sin concepto</MenuItem>
                {movementConcepts
                  .filter((concept) => concept.is_active !== false)
                  .map((concept) => (
                    <MenuItem key={concept.uuid} value={concept.uuid}>
                      {concept.name}
                    </MenuItem>
                  ))}
              </CustomAutoComplete>
            </Box>

            <Box>
              <CustomTextField
                label="Fecha de emisión"
                name="emission_date"
                type="date"
                value={formData.emission_date}
                onChange={handleInputChange}
                error={Boolean(errors.emission_date)}
                helperText={emissionDateHelperText}
                required
                shrinkLabel
                sx={{ maxWidth: '350px', mr: 1.5 }}
              />

              <CustomTextField
                label="Fecha de vencimiento"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleInputChange}
                error={Boolean(errors.due_date)}
                helperText={dueDateHelperText}
                required
                shrinkLabel
                sx={{ maxWidth: '350px', mr: 1.5 }}
              />

              <CustomTextField
                label="Importe"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                error={errors.amount}
                required
                inputProps={{ step: 1 }}
                sx={{ maxWidth: '350px' }}
              />
            </Box>

            <Box>
              <BeneficiarySelectWithCreate
                label="Beneficiario"
                name="uuid_legal_entity"
                value={formData.uuid_legal_entity}
                onChange={handleInputChange}
                legalEntities={legalEntities}
                placeholder="Sin beneficiario"
                containerSx={{ flex: 1 }}
              />
            </Box>
            <Box>
              <CustomAutoComplete
                label="Fijo/Variable"
                name="cost_behavior"
                value={formData.cost_behavior}
                onChange={handleInputChange}
                error={Boolean(errors.cost_behavior)}
                helperText={errors.cost_behavior ? classificationHelperText : ''}
                required
                sx={{ maxWidth: '220px', mr: 1.5 }}
              >
                {COST_BEHAVIOR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
              <CustomAutoComplete
                label="Ordinario/Extraordinario"
                name="cost_nature"
                value={formData.cost_nature || formData.cost_traceability}
                onChange={handleInputChange}
                error={Boolean(errors.cost_nature)}
                helperText={errors.cost_nature ? classificationHelperText : ''}
                required
                sx={{ maxWidth: '220px', mr: 1.5 }}
              >
                {COST_TRACEABILITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
              <CustomAutoComplete
                label="Área"
                name="cost_area"
                value={formData.cost_area}
                onChange={handleInputChange}
                error={Boolean(errors.cost_area)}
                helperText={errors.cost_area ? classificationHelperText : ''}
                required
                sx={{ maxWidth: '220px' }}
              >
                {COST_AREA_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
            </Box>
            <Box>
              <CustomAutoComplete
                label="Moneda"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                sx={{ maxWidth: '160px', mr: 1.5 }}
              >
                {CURRENCY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
              <CustomTextField
                label="Cotización ARS"
                name="exchange_rate_to_ars"
                value={formData.exchange_rate_to_ars}
                onChange={handleInputChange}
                sx={{ maxWidth: '180px', mr: 1.5 }}
              />
              <CustomTextField
                label="Monto ARS"
                name="amount_ars"
                value={formData.amount_ars}
                onChange={handleInputChange}
                sx={{ maxWidth: '180px' }}
              />
            </Box>
          </HorizontalForm>
        )}

        <Box sx={{ px: 3 }}>
          <CustomTextArea
            label="Observaciones"
            name="observations"
            value={formData.observations}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        saveText="Guardar"
        isLoading={isLoading}
      />
    </DialogContainer>
  );
};

export default MovementDialog;
